import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { alpha } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalActivityOutlined from "@mui/icons-material/LocalActivityOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";
import HowToRegOutlined from "@mui/icons-material/HowToRegOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined";
import { formatTime } from "../trainerScheduleHelpers";
import { statusHex, isMarked, accentFor } from "../attendanceHelpers";
import NoteCallout from "./NoteCallout";
import BookingClientList from "./BookingClientList";

const STATUS_ICON = {
  ATTENDED: CheckCircleOutlineIcon,
  NOSHOW: HighlightOffIcon,
  LMC: HighlightOffIcon,
  TBC: HighlightOffIcon
};

function BookingRow({ booking, onMark, onNote }) {
  const facilityAndLocation = [booking.facilityName, booking.locationName].filter(Boolean).join(" · ");
  const timeRange = [formatTime(booking.time), formatTime(booking.endTime)].filter(Boolean).join(" – ") || "—";
  const hasClients = booking.clients?.length > 0;
  const accent = accentFor(booking.clients);
  const noteText = booking.trainerNote?.text || "";
  const bookingNote = booking.bookingNote || "";

  return (
    <Box sx={{ py: 1.75, display: "flex", gap: { xs: 1, sm: 1.5 } }}>
      <Box
        sx={{
          flexShrink: 0,
          width: "0.25rem",
          borderRadius: 2,
          bgcolor: accent || "primary.main",
          opacity: accent ? 0.9 : 0.5
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTimeIcon sx={{ fontSize: "1.125rem", color: "text.secondary" }} />
              <Typography sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{timeRange}</Typography>
            </Box>
            {(booking.serviceName || booking.title) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", mt: 0.5 }}>
                <LocalActivityOutlined sx={{ fontSize: "1.125rem" }} />
                <Typography variant="body2" noWrap>
                  {booking.serviceName || booking.title}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
            {onNote && (
              <Tooltip title={noteText ? "Edit your note" : "Add a note"}>
                <IconButton
                  size="small"
                  color={noteText ? "primary" : "default"}
                  sx={{ p: { xs: 1, sm: 0.625 }, border: 1, borderColor: "divider", borderRadius: 1.5, ...(noteText ? {} : { color: "text.secondary" }) }}
                  onClick={() => onNote(booking)}
                  aria-label={noteText ? "Edit your note" : "Add a note"}
                >
                  <StickyNote2Outlined sx={{ fontSize: "1.125rem" }} />
                </IconButton>
              </Tooltip>
            )}

            {onMark && hasClients && (
              <Tooltip title="Attendance & levels">
                <IconButton
                  size="small"
                  color="primary"
                  sx={{ p: { xs: 1, sm: 0.625 }, border: 1, borderColor: "divider", borderRadius: 1.5 }}
                  onClick={() => onMark(booking)}
                  aria-label="Attendance and levels"
                >
                  <HowToRegOutlined sx={{ fontSize: "1.125rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {facilityAndLocation && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
              <PlaceOutlined sx={{ fontSize: "1.125rem" }} />
              <Typography variant="body2" noWrap>
                {facilityAndLocation}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
            <PeopleAltOutlined sx={{ fontSize: "1.125rem" }} />
            <Typography variant="body2">
              {booking.clientCount} {booking.clientCount === 1 ? "client" : "clients"}
            </Typography>
          </Box>
        </Stack>

        {hasClients && <BookingClientList clients={booking.clients} sx={{ mt: 1.25, display: { xs: "block", sm: "none" } }} />}

        {hasClients && (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1, display: { xs: "none", sm: "flex" } }}>
            {booking.clients.map((c) => {
              const marked = isMarked(c.status);
              const hex = statusHex(c.status);
              const Icon = STATUS_ICON[c.status];
              return (
                <Chip
                  key={c.bookishClientId || c._id}
                  size="small"
                  variant="outlined"
                  icon={marked && Icon ? <Icon sx={{ color: `${hex} !important`, fontSize: "1rem" }} /> : undefined}
                  sx={{
                    maxWidth: "100%",
                    ...(marked ? { bgcolor: alpha(hex, 0.14), color: hex, borderColor: alpha(hex, 0.4), fontWeight: 600 } : {})
                  }}
                  label={c.name}
                />
              );
            })}
          </Stack>
        )}

        {bookingNote && <NoteCallout variant="booking" text={bookingNote} sx={{ mt: 1.25 }} />}

        {noteText && <NoteCallout variant="trainer" text={noteText} sx={{ mt: 1.25 }} />}
      </Box>
    </Box>
  );
}

export default BookingRow;
